// utilities/paymentStatus.js
// ─────────────────────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH for "what payment state do we SHOW an admin for this
// order?".  Frontend mirror of the server-side gate in
// `utils/paymentConfirmation.js`.
//
// WHY THIS EXISTS (July-2026 admin payment-badge audit)
// ----------------------------------------------------
// The Orders Management table rendered its PAYMENT column from
// `order.payment_status` — a free-form, snake_case field on the Order schema
// (`models/Orders.js`) that NO mainstream payment path ever writes:
//
//   • card / Flutterwave  → writes `isPaid`, `paidAt`, `paymentStatus` (camelCase)
//   • MoMo & Airtel       → writes `isPaid`, `paidAt`
//   • rider COD collection→ writes `isPaid`, `paidAt`   (codCollectionService)
//   • admin "Mark as Paid"→ writes `isPaid`, `paidAt`   (updateOrderToPaid)
//
// `payment_status` therefore came back `undefined` for virtually every order and
// the table fell through to its "PENDING" default — so a fully-paid order showed
// PENDING in the list while the order-details page (which correctly reads
// `order.isPaid`) showed "Paid". Same order, two answers.
//
// Every admin surface that displays payment state MUST go through this module so
// the list and the detail page can never disagree again.
//
// DELIBERATE DIFFERENCE FROM THE SERVER GATE
// ------------------------------------------
// `utils/paymentConfirmation.js` is a *permissive dispatch gate*: it answers
// "may we send a rider?" and so it accepts COD and accepts the mere presence of
// `paymentResult.transactionId`. That is WRONG for a display badge.
//
// `paymentResult` is not trustworthy as evidence of payment, in two ways:
//   • `orderController.js` writes it — with a real `transactionId` — on FAILED
//     and PENDING card attempts too (the `if (safe.flwRef || data.id)` branch
//     runs regardless of outcome), so an id proves nothing; and
//   • `flutterWaveController.js` historically wrote `status: response.status`
//     into it, which is the status of the VERIFY CALL ("success" whenever the
//     API call worked, including for a declined card) rather than the
//     transaction. Rows written before July 2026 therefore say "success" on
//     failed payments.
//
// So for DISPLAY we require money to have actually landed, and we read ONLY:
//   • `isPaid === true`  — the canonical flag every real payment path writes, and
//     the exact field the order-details page shows, or
//   • an explicitly settled `paymentStatus` / `payment_status` value.
// COD gets its own badge rather than being laundered into PAID.
// ─────────────────────────────────────────────────────────────────────────────

/** Canonical display states. */
export const PAYMENT_STATE = {
  PAID: "PAID",
  COD: "COD",
  FAILED: "FAILED",
  PENDING: "PENDING",
  // A cancelled order that was never paid. Nothing is owed and nothing is
  // coming, so neither PENDING (money may still arrive) nor COD (a rider will
  // collect) is true of it (13 Oct 2026).
  VOID: "VOID",
};

// Status strings that mean "settled — the money is in".
// Spans every spelling the codebase has historically stamped:
//   COMPLETED  → orderController card success + AI gateway (canonical enum)
//   SUCCESSFUL → momo.js ticket success path
//   CONFIRMED  → conversationService chat confirm-payment
//   PAID / SUCCESS → defensive, seen on legacy rows
const SETTLED_STATUSES = new Set([
  "PAID",
  "COMPLETED",
  "COMPLETE",
  "CONFIRMED",
  "SUCCESSFUL",
  "SUCCESS",
  "SETTLED",
]);

// Status strings that mean the charge definitively did not go through.
const FAILED_STATUSES = new Set([
  "FAILED",
  "FAILURE",
  "DECLINED",
  "REJECTED",
  "CANCELLED",
  "CANCELED",
  "VOIDED",
  "EXPIRED",
]);

const norm = (value) => String(value ?? "").trim().toUpperCase();

/** Read both the canonical camelCase field and the legacy snake_case one. */
const statusCandidates = (order) => [
  norm(order?.paymentStatus), // canonical (enum: PENDING/COMPLETED/FAILED/COD)
  norm(order?.payment_status), // legacy snake_case, still written by chat + ticketing
];

/**
 * Is this order actually paid? This is the exact question the order-details
 * page answers with `order?.isPaid`, widened only to accept settled status
 * strings written by paths that (historically) forgot to set `isPaid`.
 *
 * DELIBERATELY DOES NOT TRUST `paymentResult` AT ALL
 * --------------------------------------------------
 * Two independent reasons, both verified in the backend:
 *
 *  1. `controllers/orderController.js` writes `paymentResult` (with a real
 *     `transactionId`) on FAILED and PENDING card attempts too — the
 *     `if (safe.flwRef || data.id)` branch runs regardless of outcome. So the
 *     presence of a transaction id proves nothing.
 *
 *  2. `controllers/flutterWaveController.js` historically wrote
 *     `status: response.status` into `paymentResult` — the status of the VERIFY
 *     CALL, which is "success" whenever the API call itself worked, including
 *     for a declined card. So `paymentResult.status` is "success" on failed
 *     payments in every row written before July 2026. (The live code now writes
 *     the real transaction status, but those rows are still in the database.)
 *
 * Reading either one would paint declined cards green. `isPaid` and the status
 * enums are the only trustworthy signals.
 *
 * @param {object} order
 * @returns {boolean}
 */
export const isOrderPaid = (order) =>
  order?.isPaid === true ||
  statusCandidates(order).some((s) => SETTLED_STATUSES.has(s));

/**
 * Is this a cash-on-delivery order whose cash has NOT been collected yet?
 * COD is a legitimate, dispatchable state — it is not a failure and it is not
 * "the customer hasn't paid us yet by card". It gets its own badge.
 * @param {object} order
 * @returns {boolean}
 */
export const isCodAwaitingCollection = (order) => {
  if (isOrderPaid(order)) return false;
  return (
    norm(order?.paymentMethod) === "COD" ||
    statusCandidates(order).includes("COD")
  );
};

/**
 * Did the payment explicitly fail?
 * Also honours a linked MoMo record whose own status is Failed — the paginated
 * orders endpoint populates `momo_id`, and `VendorOrderTable` already treated
 * that as authoritative.
 * @param {object} order
 * @returns {boolean}
 */
export const isPaymentFailed = (order) => {
  if (isOrderPaid(order)) return false;
  return (
    statusCandidates(order).some((s) => FAILED_STATUSES.has(s)) ||
    FAILED_STATUSES.has(norm(order?.momo_id?.status)) ||
    // Safe in THIS direction only: nothing writes a failure string here on a
    // successful charge. The reverse is not true — see `isOrderPaid`.
    FAILED_STATUSES.has(norm(order?.paymentResult?.status))
  );
};

/**
 * Is the order cancelled or rejected? `delivery_status` has no CANCELLED
 * member: every cancellation lands as REJECTED, with the audit block written
 * by services/orders/orderCancellationService.js. Either signal counts.
 * @param {object} order
 * @returns {boolean}
 */
export const isOrderCancelled = (order) =>
  norm(order?.delivery_status) === "REJECTED" || Boolean(order?.cancellation?.cancelledAt);

/**
 * The one function every admin payment badge should call.
 * Precedence is deliberate: a settled payment outranks a stale FAILED marker
 * (an admin "Mark as Paid" or a rider cash collection is the later, truer
 * event). A failed charge still shows FAILED on a cancelled order, because it
 * is usually why the order was cancelled; any other unpaid cancelled order is
 * VOID, not PENDING: a cancelled order on the dashboard read "PENDING" as if
 * money were still expected (13 Oct 2026). The server's list filter derives
 * the same five states (utils/orderListFilters.js).
 * @param {object} order
 * @returns {"PAID"|"COD"|"FAILED"|"PENDING"|"VOID"}
 */
export const getPaymentState = (order) => {
  if (isOrderPaid(order)) return PAYMENT_STATE.PAID;
  if (isPaymentFailed(order)) return PAYMENT_STATE.FAILED;
  if (isOrderCancelled(order)) return PAYMENT_STATE.VOID;
  if (isCodAwaitingCollection(order)) return PAYMENT_STATE.COD;
  return PAYMENT_STATE.PENDING;
};

/** Human label for each state — keeps wording identical across screens. */
export const PAYMENT_STATE_LABEL = {
  [PAYMENT_STATE.PAID]: "PAID",
  [PAYMENT_STATE.COD]: "COD",
  [PAYMENT_STATE.FAILED]: "FAILED",
  [PAYMENT_STATE.PENDING]: "PENDING",
  [PAYMENT_STATE.VOID]: "NOT CHARGED",
};

/** Badge theming — shared so every table renders the same colours. */
export const PAYMENT_STATE_CFG = {
  [PAYMENT_STATE.PAID]: {
    bg: "#d1fae5", color: "#065f46", border: "#a7f3d0", icon: "fa-check-circle",
  },
  [PAYMENT_STATE.COD]: {
    bg: "#dbeafe", color: "#1e40af", border: "#bfdbfe", icon: "fa-money-bill-wave",
  },
  [PAYMENT_STATE.FAILED]: {
    bg: "#fee2e2", color: "#991b1b", border: "#fecaca", icon: "fa-times-circle",
  },
  [PAYMENT_STATE.PENDING]: {
    bg: "#fef3c7", color: "#92400e", border: "#fde68a", icon: "fa-clock",
  },
  [PAYMENT_STATE.VOID]: {
    bg: "#f0f2f5", color: "#6b7c93", border: "#e4e8ef", icon: "fa-minus-circle",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Delivery-status theming — colocated so the payment and delivery badges on an
// order row are designed as one system.
//
// These keys are the COMPLETE `delivery_status` enum from models/Orders.js. The
// admin tables previously themed only 5 of the 12 and fell back to the PENDING
// (blue clock) style for the rest, so PROCESSING, DISPATCHED, IN_TRANSIT,
// ARRIVED, FULFILLED and NOT_APPLICABLE all rendered as though they were
// pending. If you add a member to the schema enum, add it here too.
//
// Note there is no "PAID" delivery status and there never was — payment state is
// a separate axis, above.
// ─────────────────────────────────────────────────────────────────────────────
export const DELIVERY_STATE_CFG = {
  PROCESSING:     { label: "Processing", bg: "#fef3c7", color: "#92400e", border: "#fde68a", icon: "fa-hourglass-half" },
  PENDING_3PL:    { label: "Awaiting 3PL", bg: "#fef3c7", color: "#92400e", border: "#fde68a", icon: "fa-hourglass-half" },
  ASSIGNED:       { label: "Assigned", bg: "#ede9fe", color: "#4c1d95", border: "#ddd6fe", icon: "fa-motorcycle" },
  ACCEPTED:       { label: "Accepted", bg: "#ede9fe", color: "#4c1d95", border: "#ddd6fe", icon: "fa-thumbs-up" },
  PICKED_UP:      { label: "Picked up", bg: "#fff3e0", color: "#92400e", border: "#fde68a", icon: "fa-box" },
  DISPATCHED:     { label: "Dispatched", bg: "#dbeafe", color: "#1e40af", border: "#bfdbfe", icon: "fa-paper-plane" },
  IN_TRANSIT:     { label: "In transit", bg: "#dbeafe", color: "#1e40af", border: "#bfdbfe", icon: "fa-route" },
  ARRIVED:        { label: "Arrived", bg: "#cffafe", color: "#155e75", border: "#a5f3fc", icon: "fa-map-marker-alt" },
  DELIVERED:      { label: "Delivered", bg: "#d1fae5", color: "#065f46", border: "#a7f3d0", icon: "fa-check-circle" },
  FULFILLED:      { label: "Fulfilled", bg: "#d1fae5", color: "#065f46", border: "#a7f3d0", icon: "fa-check-double" },
  REJECTED:       { label: "Rejected", bg: "#fee2e2", color: "#991b1b", border: "#fecaca", icon: "fa-ban" },
  NOT_APPLICABLE: { label: "No delivery", bg: "#f0f2f5", color: "#6b7c93", border: "#e4e8ef", icon: "fa-minus-circle" },
};

/** Neutral fallback for an unrecognised delivery_status (legacy/bad-data rows). */
export const DELIVERY_STATE_FALLBACK = {
  label: "Unknown", bg: "#f0f2f5", color: "#6b7c93", border: "#e4e8ef", icon: "fa-circle",
};

/**
 * Badge theme for a delivery status. An unknown value gets a neutral grey rather
 * than being disguised as PENDING.
 * @param {string} status
 */
export const getDeliveryCfg = (status) =>
  DELIVERY_STATE_CFG[String(status ?? "").trim().toUpperCase()] ||
  DELIVERY_STATE_FALLBACK;

/**
 * Human label for a delivery status.
 *
 * The cfg carried colours and icons but no words, so admin views printed the raw
 * enum next to them. Labels here stay CLOSE to the enum on purpose — this is an
 * internal tool and ops staff know what PENDING_3PL means; softening it would
 * cost them precision.
 *
 * The exception is NOT_APPLICABLE, which reads like a validation error. It is
 * the normal, correct state for a ticket order (there is no delivery leg), so
 * it says "No delivery".
 */
export const getDeliveryLabel = (status) => getDeliveryCfg(status).label;

export default getPaymentState;
